/**
 * Cookie Consent Modal Component
 * Professional, responsive modal for cookie consent following Material-UI design system
 * Complies with EU cookie consent requirements for Microsoft Clarity analytics
 */

import CookieIcon from "@mui/icons-material/Cookie";
import { Box, Dialog, DialogContent, Typography, useMediaQuery, useTheme } from "@mui/material";
import React from "react";
import { useInternationalization } from "../../contexts";
import MDButton from "../md-button";
import { useCookieConsentModal } from "./cookie-consent-modal.hook";

interface CookieConsentModalProps {
  open: boolean;
  onClose: () => void;
}

export const CookieConsentModal: React.FC<CookieConsentModalProps> = ({ open, onClose }) => {
  const { t } = useInternationalization();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { handleAccept, handleDecline } = useCookieConsentModal({ onClose });

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        // Prevent closing by clicking outside or pressing escape
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          return;
        }
        onClose();
      }}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          m: 2,
          overflow: "hidden",
          maxHeight: isMobile ? "calc(100% - 32px)" : "none",
        },
      }}
    >
      {/* Header con color de fondo */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          px: 3,
          py: 2,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <CookieIcon sx={{ fontSize: 24, color: "#ffffff" }} />
        <Typography
          variant="h6"
          sx={{
            color: "#ffffff",
            fontWeight: 600,
            fontSize: "1rem",
          }}
        >
          {t("cookie-consent.title")}
        </Typography>
      </Box>

      <DialogContent sx={{ px: 3, py: 2.5 }}>
        <Typography
          variant="body2"
          sx={{
            color: "#344767",
            mb: 1.5,
            lineHeight: 1.5,
            fontSize: "0.85rem",
          }}
        >
          {t("cookie-consent.description")}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: "#344767",
            mb: 1.5,
            lineHeight: 1.5,
            fontSize: "0.85rem",
          }}
        >
          {t("cookie-consent.purpose")}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: "#7b809a",
            fontStyle: "italic",
            display: "block",
            fontSize: "0.75rem",
          }}
        >
          {t("cookie-consent.settings-info")}
        </Typography>

        {/* Botones */}
        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            gap: 1.5,
            mt: 2.5,
            justifyContent: "flex-end",
          }}
        >
          <MDButton
            onClick={handleDecline}
            variant="outlined"
            color="secondary"
            fullWidth={isMobile}
            sx={{
              order: isMobile ? 2 : 1,
            }}
          >
            {t("cookie-consent.decline")}
          </MDButton>
          <MDButton
            onClick={handleAccept}
            variant="gradient"
            color="info"
            fullWidth={isMobile}
            sx={{
              order: isMobile ? 1 : 2,
            }}
          >
            {t("cookie-consent.accept")}
          </MDButton>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
